import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["pending_payment", "paid", "processing", "shipped", "delivered", "canceled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    api.get("/admin/orders", { params: { ...(status ? { status } : {}), ...(q ? { q } : {}) } })
      .then((r) => setOrders(r.data)).catch(() => {});
    api.post("/admin/orders-badge/seen").catch(() => {});
  };
  useEffect(load, [status]);

  const update = async (id, body) => {
    try {
      await api.patch(`/admin/orders/${id}`, body);
      toast.success("Order updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resend = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/resend-notifications`);
      toast.success("Notifications retried");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif-display text-3xl text-white font-light mb-8">Orders</h1>
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter"
          className="bg-[#0D0D0D] border border-[#D4AF37]/20 text-[#B8B8B8] font-sans-body text-xs px-3 py-2.5 focus:outline-none focus:border-[#D4AF37]">
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search order #, name or email"
          className="bg-transparent border border-[#D4AF37]/20 px-4 py-2.5 font-sans-body text-xs text-white focus:outline-none focus:border-[#D4AF37] w-64"
          data-testid="orders-search-input" />
        <button onClick={load} className="btn-outline-gold !py-2.5" data-testid="orders-search-button">Search</button>
      </div>

      <div className="space-y-3">
        {orders.length === 0 && (
          <div className="gold-hairline bg-[#0D0D0D] p-10 text-center font-sans-body text-sm text-[#B8B8B8]" data-testid="orders-empty">
            No orders yet. New orders will appear here — newest first.
          </div>
        )}
        {orders.map((o) => (
          <div key={o.id} className="gold-hairline bg-[#0D0D0D]" data-testid={`order-row-${o.id}`}>
            <button className="w-full flex flex-wrap items-center gap-4 px-5 py-4 text-left"
              onClick={() => setExpanded(expanded === o.id ? null : o.id)} data-testid={`order-expand-${o.id}`}>
              <span className="font-mono-price text-[#D4AF37] text-sm">{o.order_number}</span>
              <span className="font-sans-body text-xs text-[#B8B8B8]">{new Date(o.created_at).toLocaleString()}</span>
              <span className="font-sans-body text-xs text-white">{o.customer?.name}</span>
              <span className="font-sans-body text-xs text-[#666666]">{o.customer?.email}</span>
              <span className={`ml-auto font-sans-body text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${
                o.payment_status === "paid" ? "border-emerald-500/40 text-emerald-400" : "border-[#D4AF37]/30 text-[#C6A15B]"
              }`}>{o.status.replace(/_/g, " ")}</span>
              <span className="font-mono-price text-white text-sm">${o.total.toFixed(2)}</span>
            </button>

            {expanded === o.id && (
              <div className="px-5 pb-6 border-t border-[#D4AF37]/10 pt-5 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`order-details-${o.id}`}>
                <div className="space-y-2 font-sans-body text-sm">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] mb-3">Items</p>
                  {o.items.map((i) => (
                    <div key={`${i.product_id}-${i.size}`} className="flex justify-between text-[#B8B8B8]">
                      <span>{i.name} · {i.size} × {i.quantity}</span>
                      <span className="font-mono-price">${(i.price * i.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-[#666666] pt-2">
                    <span>Shipping ({o.shipping_method})</span><span className="font-mono-price">${o.shipping_fee.toFixed(2)}</span>
                  </div>
                  {o.tax_total > 0 && (
                    <div className="flex justify-between text-[#666666]">
                      <span>Tax</span><span className="font-mono-price">${o.tax_total.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-white pt-2 border-t border-[#D4AF37]/10">
                    <span>Total</span><span className="font-mono-price text-[#D4AF37]">${o.total.toFixed(2)}</span>
                  </div>
                  <p className="text-[10px] text-[#666666] pt-2">
                    Payment: {o.payment_status}
                    {o.owner_notified ? " · Owner emailed" : " · Owner email pending"}
                    {o.notification_error && <span className="text-red-400"> · Last email error: {o.notification_error}</span>}
                  </p>
                  {o.payment_status === "paid" && (
                    <button onClick={() => resend(o.id)} className="btn-outline-gold !py-2 !px-4 !text-[10px] mt-3" data-testid={`order-resend-${o.id}`}>
                      Retry Notifications
                    </button>
                  )}
                </div>
                <div className="space-y-4 font-sans-body text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] mb-2">Ship To</p>
                    <p className="text-[#B8B8B8] font-light">
                      {o.customer?.name}<br />
                      {o.customer?.address_line1}{o.customer?.address_line2 ? `, ${o.customer.address_line2}` : ""}<br />
                      {o.customer?.city}{o.customer?.state ? `, ${o.customer.state}` : ""} {o.customer?.postal_code}, {o.customer?.country}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] mb-2">Fulfillment Status</p>
                    <select value={o.status} onChange={(e) => update(o.id, { status: e.target.value })}
                      className="bg-[#080808] border border-[#D4AF37]/25 px-3 py-2.5 font-sans-body text-xs text-white focus:outline-none focus:border-[#D4AF37] w-full"
                      data-testid={`order-status-select-${o.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] mb-2">Tracking Number</p>
                    <div className="flex gap-2">
                      <input defaultValue={o.tracking_number || ""} id={`tracking-${o.id}`}
                        placeholder="e.g. 1Z999AA..."
                        className="flex-1 bg-transparent border border-[#D4AF37]/25 px-3 py-2.5 font-sans-body text-xs text-white focus:outline-none focus:border-[#D4AF37]"
                        data-testid={`order-tracking-input-${o.id}`} />
                      <button onClick={() => update(o.id, { tracking_number: document.getElementById(`tracking-${o.id}`).value })}
                        className="btn-outline-gold !py-2.5 !px-4 !text-[10px]" data-testid={`order-tracking-save-${o.id}`}>
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
